// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Shared hooks for renderer components.
 */
import { useQuery } from '@tanstack/react-query'
import type { NodeApiClient } from '@/app-shell/services'
import type { RendererDefinition } from '@/app-shell/types/workspace'
import { fetchMultipleDatasets, type DatasetResponse } from '@/subsystems/documents/services'
import type { DocumentSlotData } from './types'

interface UseRendererSlotDataParams {
  apiClient: NodeApiClient
  /**
   * Null while nothing is selected. Callers that need the resolved payloads
   * outside the component rendering them (e.g. a toolbar reading each slot's
   * run) hold the renderer optionally, and share this query by passing the same
   * arguments the renderer's own panel does.
   */
  renderer: RendererDefinition | null
  documentId: string
  orgId: string
  workspaceId: string
  /** Agent owning this renderer — dataset keys are only unique within it. */
  agentId: string
  version?: number
  /** Skip fetching when false (e.g. compare-mode components fetch internally). */
  enabled?: boolean
}

export interface RendererSlotDataResult {
  /** Payload by slot key. Slots whose dataset wasn't found map to null. */
  slotData: Record<string, DatasetResponse | null>
  /** Slot keys that returned a payload (i.e. the dataset exists at this version). */
  presentSlotKeys: string[]
  /** Slot keys whose dataset wasn't found at this version. */
  missingSlotKeys: string[]
  loading: boolean
  error: string | null
}

/**
 * Fetches the dataset payload for every `type: 'dataset'` slot bound on the
 * renderer, in parallel, at the given document version. Returns slot-keyed
 * payloads plus presence/missing lists for "missing data" UI.
 *
 * Document-text slots are not fetched — components handle streaming text
 * themselves.
 */
export function useRendererSlotData({
  apiClient,
  renderer,
  documentId,
  orgId,
  workspaceId,
  agentId,
  version,
  enabled = true,
}: UseRendererSlotDataParams): RendererSlotDataResult {
  const datasetSlots = (renderer?.slots ?? []).filter(
    s => s.type === 'dataset' && Boolean(s.datasetKey),
  )
  const datasetKeys = datasetSlots.map(s => s.datasetKey!).sort()
  const queryEnabled =
    enabled && Boolean(documentId && orgId && workspaceId) && datasetKeys.length > 0

  const { data, isPending, error } = useQuery({
    queryKey: [
      'renderer-slot-data',
      orgId,
      workspaceId,
      documentId,
      agentId,
      version ?? 'latest',
      datasetKeys,
    ],
    queryFn: async () => {
      const map = await fetchMultipleDatasets(
        apiClient,
        orgId,
        workspaceId,
        documentId,
        agentId,
        datasetKeys,
        version,
      )
      const out: Record<string, DatasetResponse | null> = {}
      for (const slot of datasetSlots) {
        out[slot.key] = map.get(slot.datasetKey!) ?? null
      }
      return out
    },
    enabled: queryEnabled,
  })

  const slotData = data ?? {}
  const presentSlotKeys: string[] = []
  const missingSlotKeys: string[] = []
  for (const slot of datasetSlots) {
    if (slotData[slot.key]) presentSlotKeys.push(slot.key)
    else if (data) missingSlotKeys.push(slot.key)
  }

  return {
    slotData,
    presentSlotKeys,
    missingSlotKeys,
    loading: queryEnabled && isPending,
    error: error instanceof Error ? error.message : null,
  }
}

interface UseRendererSlotDataMultiParams {
  apiClient: NodeApiClient
  renderer: RendererDefinition
  /**
   * Documents to read. `datasetKeys` narrows the fetch to the datasets that document is
   * known to have results for — a renderer bound to several datasets over a set of documents
   * would otherwise request every pair, and 404 for each one that never existed. Omit it to
   * request all of the renderer's datasets.
   */
  documents: Array<{
    documentId: string
    documentName?: string
    version: number
    datasetKeys?: string[]
  }>
  orgId: string
  workspaceId: string
  /** Agent owning this renderer — dataset keys are only unique within it. */
  agentId: string
  /**
   * Optional group id passed through to the dataset endpoint. DocumentAnalysis
   * rows are partitioned by group, so omitting this falls through to the `_`
   * default partition and 404s for grouped documents (e.g. SRM requests).
   */
  groupId?: string
  enabled?: boolean
}

export interface RendererSlotDataMultiResult {
  documentSlotData: DocumentSlotData[]
  /** True if at least one document returned a non-null payload for any slot. */
  hasResults: boolean
  loading: boolean
  error: string | null
}

/**
 * Aggregate variant of `useRendererSlotData`: fetches every dataset slot for
 * every document in parallel. Used by `versionMode: 'aggregate'` renderers.
 */
export function useRendererSlotDataMulti({
  apiClient,
  renderer,
  documents,
  orgId,
  workspaceId,
  agentId,
  groupId,
  enabled = true,
}: UseRendererSlotDataMultiParams): RendererSlotDataMultiResult {
  const datasetSlots = renderer.slots.filter(
    s => s.type === 'dataset' && Boolean(s.datasetKey),
  )
  const datasetKeys = datasetSlots.map(s => s.datasetKey!).sort()
  const docVersions = documents
    .map(d => `${d.documentId}:${d.version}:${(d.datasetKeys ?? datasetKeys).join(',')}`)
    .sort()
  const queryEnabled =
    enabled
    && Boolean(orgId && workspaceId)
    && datasetKeys.length > 0
    && documents.length > 0

  const { data, isPending, error } = useQuery({
    queryKey: [
      'renderer-slot-data-multi',
      orgId,
      workspaceId,
      agentId,
      groupId ?? null,
      docVersions,
      datasetKeys,
    ],
    queryFn: async () => {
      const out: DocumentSlotData[] = await Promise.all(
        documents.map(async (doc) => {
          const docKeys = doc.datasetKeys ?? datasetKeys
          const map = await fetchMultipleDatasets(
            apiClient,
            orgId,
            workspaceId,
            doc.documentId,
            agentId,
            docKeys,
            doc.version,
            groupId,
          )
          const slotData: Record<string, DatasetResponse | null> = {}
          for (const slot of datasetSlots) {
            slotData[slot.key] = map.get(slot.datasetKey!) ?? null
          }
          return {
            documentId: doc.documentId,
            documentName: doc.documentName,
            version: doc.version,
            slotData,
          }
        }),
      )
      return out
    },
    enabled: queryEnabled,
  })

  const documentSlotData = data ?? []
  const hasResults = documentSlotData.some(d =>
    Object.values(d.slotData).some(v => v !== null),
  )

  return {
    documentSlotData,
    hasResults,
    loading: queryEnabled && isPending,
    error: error instanceof Error ? error.message : null,
  }
}
